import {setCookie, unsetCookie} from '@wandb/weave/common/util/cookie';
import queryString from 'query-string';
import {useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {type Store} from 'redux';

import {clearCurrentIdToken} from '../actions/auth';
import {apolloClient} from '../apolloClient';
import {AccountSelectorStorageKeys} from '../components/Search/SearchNav/AccountSelectorContextProvider';
import {urlPrefixed} from '../config';
import {CREATE_ENTITY, ENTITY_QUERY} from '../graphql/users';
import {
  CHANGE_PASSWORD_PATH,
  LOGIN_PATH,
  LOGOUT_PATH,
} from '../routes/auth/authPaths';
import {ONBOARDING_FLOW_PATH} from '../routes/onboarding/onboardingPaths';
import {displayError} from '../state/global/actions';
import {reloadCurrentViewer, setCurrentViewer} from '../state/viewer/actions';
import {type JWTClaims} from '../state/viewer/types';
import {getCache} from './cache';
import {propagateErrorsContext} from './errors';
import {safeLocalStorage} from './localStorage';
import {forceRedirect} from './navigation';
import {
  isOnReportView,
  login,
  loginEndpoint,
  type LoginParams,
  logout,
  stripOriginFromURL,
} from './urls';
import {isInIframe} from './window';

export class Auth {
  // we only want to attempt auto login once per page load
  _autoLoginAttempted = false;
  _loggingOut = false;
  tempKey: string | undefined;
  claims?: JWTClaims;

  store: Store | null = null;

  constructor() {
    // We store the temporary key for password resets and invites.
    const params = queryString.parse(document.location.search);
    if (params.jwt) {
      this.tempKey = params.jwt as string;
    }
  }

  login(options?: LoginParams, state?: any) {
    let backTo = '';

    if (state && state.from && state.from.pathname !== LOGIN_PATH) {
      backTo =
        state.from.pathname +
        (state.from.search || '') +
        (state.from.hash || '');
      setAuthRedirect(backTo);
    } else {
      if (!document.location.href.endsWith('no-access')) {
        setAuthRedirect(document.location.href);
      }
    }

    // The backend will redirect to the login modal or the SSO provider
    // we need to open a new window if we're logging in from an iframe.
    if (isInIframe()) {
      window.open(login(options));
    } else {
      forceRedirect(login(options));
    }
  }

  async logout(redirect = true) {
    // This can get called in a loop, only logout if we're logged in.  Maintain
    // loggingOut state in this object
    if (this.loggedIn() && !this._loggingOut) {
      this._loggingOut = true;
      try {
        safeLocalStorage.removeItem(
          AccountSelectorStorageKeys.LAST_ACCOUNT_SELECTOR_VALUE
        );
        safeLocalStorage.removeItem(
          AccountSelectorStorageKeys.LAST_NON_IMPERSONATED_ACCOUNT
        );
        this.store!.dispatch(clearCurrentIdToken());
        // Hack: set `loading` (in `setCurrentViewer`) to true so that App.tsx
        // renders a Loader. This hides us flashing the Login page (PrivateRoute
        // redirects to /login because we're not logged in).
        this.store!.dispatch(setCurrentViewer(true, undefined));
        await fetch(logout(), {
          method: 'DELETE',
          credentials: 'include',
        });
        // clear the longterm cache on logout
        const cache = getCache();
        await cache.clear();
        if (redirect) {
          window.location.href = urlPrefixed();
        }
      } catch (e) {
        console.error('Unable to logout: ', e);
      }
      this._loggingOut = false;
    }
  }

  parseClaims(jwt: string) {
    if (jwt === 'dummy') {
      // this is effectively a passthrough for the integration tests
      // it'll only work in integration because it relies on gorilla's
      // mock auth header, which is otherwise disabled
      return {exp: Date.now() + 60 * 60 * 24};
    }
    try {
      return JSON.parse(atob(jwt.split('.')[1]));
    } catch (e) {
      return {exp: 0};
    }
  }

  loggedIn(): boolean {
    // We determine if we're logged in from the viewer in our redux store
    // App.tsx is careful to not call .loggedIn until we've made the initial
    // viewer request
    const state = this.store?.getState() ?? {viewer: {}};
    return !!state.viewer.viewer;
  }

  async remoteLogin(email: string, password: string, username?: string) {
    const response = await fetch(loginEndpoint(), {
      method: 'POST',
      body: JSON.stringify({
        email,
        username,
        password,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if ([401, 403].indexOf(response.status) > -1) {
      return false;
    } else if (response.status !== 200) {
      this.store!.dispatch(
        displayError({
          code: 500,
          message:
            'Authentication service is down, ask your administrator to check system logs',
        })
      );
      return false;
    }
    this.tempKey = undefined;
    return true;
  }

  async changePassword(password: string, email?: string, username?: string) {
    const claims = this.store!.getState().viewer.claims;
    const originalEmail = claims?.originalEmail ?? 'local@wandb.com';
    const userUpdates: Array<{[key: string]: any}> = [
      {email: email || originalEmail, password},
    ];
    if (claims?.reset && email) {
      userUpdates.push({
        email: originalEmail,
        delete: true,
      });
    }
    try {
      const reset = await fetch(urlPrefixed('/oidc/users'), {
        method: 'PUT',
        body: JSON.stringify(userUpdates),
        headers: {
          Authorization: this.tempKey ? `Bearer ${this.tempKey}` : '',
          'Content-Type': 'application/json',
        },
      });
      if (reset.status === 401 || reset.status === 403) {
        this.store!.dispatch(
          displayError({
            code: 403,
            message:
              'Invitation code expired, ask your administrator for a new invite',
          })
        );
        return false;
      } else if (reset.status !== 200) {
        this.store!.dispatch(
          displayError({
            code: reset.status,
            message: 'Unable to create account, contact your administrator.',
          })
        );
        return false;
      }
    } catch (e) {
      console.error('error: ', e);
      this.store!.dispatch(
        displayError({
          code: 500,
          message: 'Unable to change password, try refreshing and try again',
        })
      );
      return false;
    }

    await this.remoteLogin(email || originalEmail, password, username);
    this.store!.dispatch(reloadCurrentViewer());
    return true;
  }

  async autoLogin() {
    /* Attempts to login with the default user, called from index.tsx */
    if (
      !this.loggedIn() &&
      !this._autoLoginAttempted &&
      !window.location.pathname.endsWith(LOGOUT_PATH)
    ) {
      try {
        this._autoLoginAttempted = true;
        const success = await this.remoteLogin('local@wandb.com', 'perceptron');
        if (success) {
          console.log('Login succeeded');
          const res = await apolloClient.query({
            context: propagateErrorsContext(),
            query: ENTITY_QUERY,
            variables: {name: 'local'},
          });
          if (res.data.entity.available) {
            await apolloClient.mutate({
              context: propagateErrorsContext(),
              mutation: CREATE_ENTITY,
              variables: {name: 'local'},
            });
          }
          // Reload our viewer into redux, with reset in the claims
          this.store!.dispatch(
            reloadCurrentViewer({
              reset: true,
              originalEmail: 'local@wandb.com',
            })
          );
        }
      } catch (e) {
        console.error(e);
      }
    } else if (this.loggedIn()) {
      // if we're already logged in as the local default user (this can happen if
      // you refresh the page after starting local signup), force us back into the
      // local signup state
      const state = this.store?.getState() ?? {viewer: {}};
      if (state.viewer.viewer.email === 'local@wandb.com') {
        this.store!.dispatch(
          reloadCurrentViewer({
            reset: true,
            originalEmail: 'local@wandb.com',
          })
        );
      }
    }
  }

  async ensureCurrentIdToken() {
    // Use the temporary key if it exists otherwise the auth will be in an HTTP only cookie
    return this.tempKey;
  }
}

export const AUTH_REDIRECT_KEY = 'auth.redirect';
export const IS_FOLLOWING_INVITE_LINK_KEY = 'auth.followingInviteLink';

export function setIsFollowingInviteLink(isFollowingInviteLink: boolean): void {
  safeLocalStorage.setItem(
    IS_FOLLOWING_INVITE_LINK_KEY,
    isFollowingInviteLink.toString()
  );
}

export function getIsFollowingInviteLink(): boolean {
  return safeLocalStorage.getItem(IS_FOLLOWING_INVITE_LINK_KEY) === 'true';
}

export function clearIsFollowingInviteLink(): void {
  safeLocalStorage.removeItem(IS_FOLLOWING_INVITE_LINK_KEY);
}

export function getAuthRedirect(): string {
  const url =
    window.__WB_authRedirect ??
    safeLocalStorage.getItem(AUTH_REDIRECT_KEY) ??
    '/';
  return stripOriginFromURL(url);
}

export function setAuthRedirect(url: string): void {
  const urlWithoutOrigin = stripOriginFromURL(url);
  if (!isValidAuthRedirect(urlWithoutOrigin)) {
    return;
  }
  window.__WB_authRedirect = urlWithoutOrigin;
  safeLocalStorage.setItem(AUTH_REDIRECT_KEY, urlWithoutOrigin);
}

export function clearAuthRedirect(): void {
  delete window.__WB_authRedirect;
  safeLocalStorage.removeItem(AUTH_REDIRECT_KEY);
}

/**
 * Redirects the user to the auth URL using the default browser navigation.
 */
export function doAuthRedirect(): string {
  const url = getAuthRedirect();
  clearAuthRedirect();
  window.location.replace(urlPrefixed(url));
  return url;
}

/**
 * Redirect the user to the auth URL using the current react router history object.
 */
export function useAuthRedirect() {
  const history = useHistory();
  const authRedirect = () => {
    const url = getAuthRedirect();
    clearAuthRedirect();
    history.replace(url);
  };
  return {authRedirect};
}

export function redirectToSignupThenBackHere(
  streamlinedOnboarding: boolean = false
) {
  setAuthRedirect(window.location.pathname);
  if (streamlinedOnboarding) {
    // Set LS flag to indicate that onboarding should be streamlined
    safeLocalStorage.setItem('streamlinedOnboarding', 'true');
  }
  window.location.replace(login({signup: true}));
}

const INVALID_REDIRECT_PATHS = [
  LOGIN_PATH,
  LOGOUT_PATH,
  ONBOARDING_FLOW_PATH,
  CHANGE_PASSWORD_PATH,
  '/no-access',
];

function isValidAuthRedirect(url: string): boolean {
  for (const path of INVALID_REDIRECT_PATHS) {
    if (url.startsWith(path)) {
      return false;
    }
  }
  return true;
}

const ACCESS_TOKEN_COOKIE_KEY = 'access_token';
const REPORT_VIEW_COOKIE_KEY = 'report-view';

export function setAccessTokenCookie(token: string): void {
  setCookie(ACCESS_TOKEN_COOKIE_KEY, token);
}

export function unsetAccessTokenCookie(): void {
  unsetCookie(ACCESS_TOKEN_COOKIE_KEY);
}

export function setReportViewCookie(): void {
  setCookie(REPORT_VIEW_COOKIE_KEY, 'true');
}

export function unsetReportViewCookie(): void {
  unsetCookie(REPORT_VIEW_COOKIE_KEY);
}

export function setAccessTokenCookieFromQS(): void {
  const {accessToken} = queryString.parse(window.location.search);
  if (accessToken && isOnReportView()) {
    setAccessTokenCookie(accessToken as string);
    setReportViewCookie();
  } else if (isOnReportView()) {
    setReportViewCookie();
  } else {
    unsetAccessTokenCookie();
    unsetReportViewCookie();
  }
}

// (un)set access token cookie on page change
// THIS IS NECESSARY SO USERS CAN'T ACCESS OTHER PAGES IN PRIVATE
// PROJECTS AFTER LOADING A REPORT WITH AN ACCESS TOKEN
export function useSetAccessTokenFromCookieFromQS() {
  const history = useHistory();
  useEffect(() => {
    // (un)set access token cookie on page load
    setAccessTokenCookieFromQS();

    const unregister = history.listen(() => {
      setAccessTokenCookieFromQS();
    });

    return () => {
      unregister();
    };
  }, [history]);
}
